import { useEffect, useRef, useState } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';

/**
 * Aperçu en direct — reproduction du LiveScreenPro (tableau de départs).
 * Le mock est dessiné sur un canvas fixe 960×540 puis mis à l'échelle,
 * ce qui permet un vrai plein écran net (requestFullscreen).
 */
const LivePreview = (props: {
    text1: string;
    text2: string;
    logo: string;
    color1: string;
    color2: string;
    subtitle?: string;
}) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(0.4);
    const [isFs, setIsFs] = useState(false);

    const accent = props.color1 || '#4338CA';
    const accent2 = props.color2 || '#3730A3';

    /* Échelle : le canvas 960×540 s'adapte à son conteneur */
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => {
            const r = el.getBoundingClientRect();
            setScale(Math.min(r.width / 960, r.height / 540));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    /* Plein écran */
    useEffect(() => {
        const onFs = () => setIsFs(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onFs);
        return () => document.removeEventListener('fullscreenchange', onFs);
    }, []);

    const toggleFull = () => {
        if (document.fullscreenElement) document.exitFullscreen();
        else wrapRef.current?.requestFullscreen?.();
    };

    const rows = [
        { box: '1', ticket: 'A022' },
        { box: '3', ticket: '' },
        { box: '4', ticket: 'B011' },
        { box: '5', ticket: '' },
    ];
    const next = ['A024', 'A025', 'B012'];

    return (
        <div className="eb-live-preview">
            <div className="eb-live-preview-label">
                Aperçu — Live Screen Pro
                <button type="button" className="eb-lpp-fs-btn" onClick={toggleFull}
                    title={isFs ? 'Quitter le plein écran' : 'Agrandir en plein écran'}>
                    {isFs ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                </button>
            </div>

            <div ref={wrapRef} className={`eb-lpp-wrap${isFs ? ' is-fs' : ''}`}>
                <div
                    className="eb-lpp-canvas"
                    style={{
                        transform: `scale(${scale})`,
                        background:
                            `radial-gradient(130% 110% at 0% 0%, ${accent}33, transparent 55%),` +
                            `radial-gradient(130% 110% at 100% 100%, ${accent2}2e, transparent 55%),` +
                            `#070a13`,
                    }}
                >
                    {/* Halos */}
                    <span className="eb-lpp-glow" style={{ top: '-18%', left: '-8%', background: `radial-gradient(closest-side, ${accent}4d, transparent 72%)` }} />
                    <span className="eb-lpp-glow" style={{ bottom: '-20%', right: '-8%', background: `radial-gradient(closest-side, ${accent2}40, transparent 72%)` }} />

                    {/* Barre flottante */}
                    <header className="eb-lpp-bar">
                        <div className="eb-lpp-bar-brand">
                            {props.logo
                                ? <img src={props.logo} alt="logo" className="eb-lpp-bar-logo" />
                                : <span className="eb-lpp-bar-logo is-ph">Logo</span>}
                            <div className="eb-lpp-bar-text">
                                <span className="eb-lpp-bar-t1">{props.text1 || 'Texte 1'}</span>
                                <span className="eb-lpp-bar-name">Nom abonné</span>
                            </div>
                        </div>
                        <div className="eb-lpp-bar-right">
                            <span className="eb-lpp-live"><i /> EN DIRECT</span>
                            <div className="eb-lpp-clock">
                                <span className="eb-lpp-clock-time">12:30:45</span>
                                <span className="eb-lpp-clock-date">ven. 20 mars</span>
                            </div>
                        </div>
                    </header>

                    {/* Corps : stage pub + board */}
                    <main className="eb-lpp-main">
                        <section className="eb-lpp-stage">
                            <div className="eb-lpp-stage-frame">
                                <span className="eb-lpp-stage-ph">Publicités</span>
                            </div>
                        </section>
                        <aside className="eb-lpp-board">
                            <div className="eb-lpp-now" style={{ borderColor: `${accent}66` }}>
                                <span className="eb-lpp-now-head">Appel en cours</span>
                                <div className="eb-lpp-now-body">
                                    <span className="eb-lpp-now-ticket">A023</span>
                                    <span className="eb-lpp-now-arrow" style={{ color: accent }}>→</span>
                                    <span className="eb-lpp-now-box">Guichet <b>2</b></span>
                                </div>
                            </div>
                            <div className="eb-lpp-rows">
                                <div className="eb-lpp-rows-head"><span>Guichet</span><span>Client</span></div>
                                {rows.map((r, i) => (
                                    <div key={i} className={`eb-lpp-row${r.ticket ? ' is-on' : ''}`}>
                                        <span className="eb-lpp-row-box">
                                            <i className="eb-lpp-dot" style={{ background: r.ticket ? accent : 'rgba(148,163,184,.4)' }} />
                                            {r.box}
                                        </span>
                                        <span className="eb-lpp-row-ticket">{r.ticket || '—'}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="eb-lpp-next">
                                <span className="eb-lpp-next-label">Suivants</span>
                                <div className="eb-lpp-next-row">
                                    {next.map((n, i) => (
                                        <span key={i} className="eb-lpp-next-chip"
                                            style={{ borderColor: `${accent}44`, background: `${accent}14` }}>{n}</span>
                                    ))}
                                </div>
                            </div>
                        </aside>
                    </main>

                    {/* Ticker */}
                    <footer className="eb-lpp-ticker">
                        <span className="eb-lpp-ticker-tag" style={{ background: accent }}>INFO</span>
                        <div className="eb-lpp-ticker-text"><span>{props.text2 || 'Message défilant'}</span></div>
                    </footer>
                </div>
            </div>
        </div>
    );
};

export default LivePreview;
